import { alpha } from '@mui/material';
import Colors from '../../styles/Colors';

// ============================================================================
// CONSTANTS
// ============================================================================

export const THEME_COLORS = {
  primary: Colors.blue, // #0D6DFF
  success: Colors.green, // #138760
  error: Colors.red, // #A00C10
  warning: '#ED8936',
  text: {
    primary: '#1A202C',
    secondary: '#4A5568',
    tertiary: '#718096',
    quaternary: '#A0AEC0',
  },
  background: {
    page: '#FAFAFA',
    card: Colors.trueWhite, // #FFFFFF
    hover: alpha(Colors.blue, 0.04),
    section: alpha(Colors.blue, 0.03),
  },
  border: {
    default: '#E2E8F0',
    accent: Colors.blue,
  },
};

export const EDIT_TYPES = {
  ALL: 'all',
  ACCEPT: 'accept',
  REJECT: 'reject',
  REVISE: 'revise',
};

export const STATUS_TYPES = {
  ALL: 'all',
  PENDING: 'pending',
  APPROVED: 'approved',
  DISAPPROVED: 'disapproved',
};

export const SORT_FIELDS = {
  SIMILARITY: 'similarity',
  STATUS: 'status',
  SECTION: 'section',
};

export const SIMILARITY_THRESHOLDS = {
  HIGH: 0.9,
  MEDIUM: 0.8,
};

export const CHIP_STYLES = {
  base: {
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    bgcolor: '#E2E8F0',
    color: THEME_COLORS.text.secondary,
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    },
  },
  selected: {
    fontWeight: 600,
    bgcolor: THEME_COLORS.primary,
    color: THEME_COLORS.background.card,
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get section name(s) from a sentence object
 * @param {Object} sentence - The sentence object
 * @returns {string[]} Array of section names
 */
export const getSectionNames = (sentence) => {
  if (!sentence) {
    return ['Unknown'];
  }

  if (Array.isArray(sentence.sections)) {
    return sentence.sections.map((s) =>
      typeof s === 'string' ? s : s?.name || s?.id || 'Unknown'
    );
  }
  return sentence.section ? [sentence.section] : ['Unknown'];
};

/**
 * Determine edit type based on original and edited text
 * @param {Object} sentence - The sentence object
 * @returns {string} Edit type: 'accept', 'reject', or 'revise'
 */
export const getEditType = (sentence) => {
  if (!sentence) {
    return EDIT_TYPES.REVISE;
  }

  const original = (sentence.value || sentence.original_text || '').trim();
  const edited = (sentence.edited_value || sentence.edited_text || '').trim();

  // Reject: edited sentence is blank
  if (!edited) {
    return EDIT_TYPES.REJECT;
  }

  // Accept: original and edited sentences match
  if (original === edited) {
    return EDIT_TYPES.ACCEPT;
  }

  // Revise: original and edited sentences are different
  return EDIT_TYPES.REVISE;
};

/**
 * Get color for similarity score
 * @param {number} score - Similarity score (0-1)
 * @returns {string} Color string ('success', 'warning', or 'error')
 */
export const getScoreColor = (score) => {
  if (typeof score !== 'number' || isNaN(score)) {
    return 'error';
  }

  if (score >= SIMILARITY_THRESHOLDS.HIGH) return 'success';
  if (score >= SIMILARITY_THRESHOLDS.MEDIUM) return 'warning';
  return 'error';
};

/**
 * Get label for similarity score
 * @param {number} score - Similarity score (0-1)
 * @returns {string} Label text ('High', 'Medium', 'Low', or 'Unknown')
 */
export const getScoreLabel = (score) => {
  if (typeof score !== 'number' || isNaN(score)) {
    return 'Unknown';
  }

  if (score >= SIMILARITY_THRESHOLDS.HIGH) return 'High';
  if (score >= SIMILARITY_THRESHOLDS.MEDIUM) return 'Medium';
  return 'Low';
};

/**
 * Extract unique sections from all sentences
 * @param {Array} similarSentences - Array of sentence objects
 * @returns {string[]} Sorted array of unique section names
 */
export const extractAvailableSections = (similarSentences) => {
  if (!Array.isArray(similarSentences) || similarSentences.length === 0) {
    return [];
  }

  const sectionsSet = new Set();
  similarSentences.forEach((sentence) => {
    if (!sentence) return;

    if (Array.isArray(sentence.sections)) {
      sentence.sections.forEach((s) => {
        const sectionName = typeof s === 'string' ? s : s?.name || s?.id || 'Unknown';
        sectionsSet.add(sectionName);
      });
    } else if (sentence.section) {
      sectionsSet.add(sentence.section);
    }
  });

  return Array.from(sectionsSet).sort();
};

/**
 * Filter sentences based on edit type, status, and section filters
 * @param {Array} sentences - Array of sentence objects to filter
 * @param {string[]} editTypeFilter - Array of edit types to include
 * @param {string[]} statusFilter - Array of statuses to include
 * @param {string[]} sectionFilter - Array of section names to include
 * @returns {Array} Filtered array of sentences
 */
export const filterSentences = (
  sentences,
  editTypeFilter,
  statusFilter,
  sectionFilter
) => {
  if (!Array.isArray(sentences)) {
    return [];
  }

  let filtered = sentences;

  // Filter by edit type
  if (
    Array.isArray(editTypeFilter) &&
    !editTypeFilter.includes(EDIT_TYPES.ALL) &&
    editTypeFilter.length > 0
  ) {
    filtered = filtered.filter((sentence) => {
      return editTypeFilter.includes(getEditType(sentence));
    });
  }

  // Filter by status
  if (
    Array.isArray(statusFilter) &&
    !statusFilter.includes(STATUS_TYPES.ALL) &&
    statusFilter.length > 0
  ) {
    filtered = filtered.filter((sentence) => {
      const status = sentence?.status || STATUS_TYPES.PENDING;
      return statusFilter.includes(status);
    });
  }

  // Filter by section
  if (Array.isArray(sectionFilter) && sectionFilter.length > 0) {
    filtered = filtered.filter((sentence) => {
      const sectionNames = getSectionNames(sentence);
      return sectionNames.some((name) => sectionFilter.indexOf(name) !== -1);
    });
  }

  return filtered;
};

/**
 * Sort sentences based on sort field and order
 * @param {Array} sentences - Array of sentence objects to sort
 * @param {string} sortBy - Field to sort by ('similarity', 'status', or 'section')
 * @param {string} sortOrder - Sort direction ('asc' or 'desc')
 * @returns {Array} Sorted array of sentences (new array reference)
 */
export const sortSentences = (sentences, sortBy, sortOrder) => {
  if (!Array.isArray(sentences)) {
    return [];
  }

  return [...sentences].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case SORT_FIELDS.SIMILARITY:
        aValue = a?.score || 0;
        bValue = b?.score || 0;
        break;
      case SORT_FIELDS.STATUS: {
        // Status priority: pending > approved > disapproved
        const statusPriority = {
          [STATUS_TYPES.PENDING]: 0,
          [STATUS_TYPES.APPROVED]: 1,
          [STATUS_TYPES.DISAPPROVED]: 2,
        };
        aValue = statusPriority[a?.status || STATUS_TYPES.PENDING];
        bValue = statusPriority[b?.status || STATUS_TYPES.PENDING];
        break;
      }
      case SORT_FIELDS.SECTION:
        aValue = getSectionNames(a).join(', ');
        bValue = getSectionNames(b).join(', ');
        break;
      default:
        return 0;
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });
};
